import { useState, useCallback } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts'
import {
  ArrowUpRight,
  ArrowDownRight,
  Package,
  Users,
  TrendingUp,
  MapPin,
  Clock,
  CheckCircle2,
  Activity,
  MoreHorizontal,
  ChevronRight,
  Plus,
  RefreshCw,
  Loader2,
  Box,
  Award,
  Download
} from 'lucide-react'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { useDashboardStats } from '@/services/stats-service'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'

// ============================================================================
// MOCK DATA - SKG METAL
// ============================================================================

// Günlük veriler - Son 7 gün
// Mock data removed in favor of real-time API data

// ============================================================================
// COMPONENTS
// ============================================================================

// Helper components
function StatCard({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  iconColor = 'text-muted-foreground',
  subtitle,
  trendData,
  variant,
  loading,
}: {
  title: string
  value: string
  change?: string
  changeType?: 'up' | 'down' | 'neutral'
  icon: React.ElementType
  iconColor?: string
  subtitle?: string
  trendData?: number[]
  variant?: 'default' | 'warning' | 'destructive' | 'success'
  loading?: boolean
}) {
  if (loading) {
    return (
      <Card className="relative overflow-hidden">
        <CardContent className="flex h-[120px] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  const maxTrend = trendData && trendData.length > 0 ? Math.max(...trendData) : 0

  const variantStyles = {
    default: '',
    warning: 'bg-yellow-500/10 border-yellow-500/20',
    destructive: 'bg-red-500/10 border-red-500/20',
    success: 'bg-green-500/10 border-green-500/20',
  }

  const iconVariantStyles = {
    default: 'bg-muted',
    warning: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
    destructive: 'bg-red-500/20 text-red-600 dark:text-red-400',
    success: 'bg-green-500/20 text-green-600 dark:text-green-400',
  }

  return (
    <Card className={`relative overflow-hidden ${variant ? variantStyles[variant] : ''}`}>
      <CardHeader className="flex flex-row items-center justify-between pb-2 p-4 sm:p-6 sm:pb-2">
        <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className={`rounded-lg p-1.5 sm:p-2 ${variant ? iconVariantStyles[variant] : iconColor}`}>
          <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        <div className="text-xl sm:text-2xl font-bold">{value}</div>
        {(change || subtitle) && (
          <div className="mt-1 flex items-center gap-1.5 sm:gap-2 flex-wrap">
            {change && changeType && (
              <span className={`flex items-center text-[10px] sm:text-xs font-medium ${changeType === 'up' ? 'text-primary' :
                changeType === 'down' ? 'text-destructive' : 'text-muted-foreground'
                }`}>
                {changeType === 'up' && <ArrowUpRight className="mr-0.5 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                {changeType === 'down' && <ArrowDownRight className="mr-0.5 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                {change}
              </span>
            )}
            {subtitle && <span className="text-[10px] sm:text-xs text-muted-foreground">{subtitle}</span>}
          </div>
        )}
        {trendData && (
          <div className="mt-3 flex items-end gap-1 h-8">
            {trendData.map((val, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-primary/20"
                style={{ height: `${(val / maxTrend) * 100}%`, minHeight: '4px' }}
              />
            ))}
          </div>
        )}
      </CardContent>
      <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full ${iconColor} opacity-5`} />
    </Card>
  )
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; dot: string }> = {
    'Aktif': { bg: 'bg-primary/10', text: 'text-primary', dot: 'bg-primary' },
    'Taslak': { bg: 'bg-muted', text: 'text-muted-foreground', dot: 'bg-yellow-500' },
    'Yeni': { bg: 'bg-destructive/10', text: 'text-destructive', dot: 'bg-destructive' },
  }

  const style = config[status] || { bg: 'bg-muted', text: 'text-muted-foreground', dot: 'bg-muted-foreground' }

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${style.bg} ${style.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
      {status}
    </span>
  )
}

// Widgets removed

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Panel() {
  const { data: stats, isLoading: isStatsLoading } = useDashboardStats()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    setIsRefreshing(false)
    toast.success('Veriler güncellendi')
  }, [queryClient])

  const handleExportReport = useCallback(async () => {
    setIsExporting(true)
    try {
      const api = (await import('@/lib/axios')).default
      const { data } = await api.get('/report')

      const wb = XLSX.utils.book_new()
      const loc = (val: any) => typeof val === 'object' ? (val?.tr || val?.en || '') : (val || '')
      const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''
      const statusMap: Record<string, string> = { new: 'Yeni', in_progress: 'İşlemde', resolved: 'Çözüldü', reviewing: 'İnceleniyor', accepted: 'Kabul', rejected: 'Reddedildi' }

      // Sheet 1: Özet
      const summaryData = [
        ['SKG Metal - Tam Veri Raporu'],
        ['Oluşturulma Tarihi', new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })],
        [],
        ['Metrik', 'Değer'],
        ['Toplam Ürün', data.products?.length || 0],
        ['Toplam Kategori', data.categories?.length || 0],
        ['Blog Yazıları', data.blogPosts?.length || 0],
        ['Partnerler', data.partners?.length || 0],
        ['Ekip Üyeleri', data.teamMembers?.length || 0],
        ['İletişim Formları', data.contacts?.length || 0],
        ['Kariyer Başvuruları', data.applications?.length || 0],
      ]
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
      summarySheet['!cols'] = [{ wch: 32 }, { wch: 28 }]
      summarySheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }]
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Özet')

      // Sheet 2: Tüm Ürünler
      if (data.products?.length) {
        const prodRows = data.products.map((p: any) => [
          loc(p.title),
          loc(p.slug),
          loc(p.description)?.substring(0, 200),
          Array.isArray(p.categoryIds) ? p.categoryIds.map((c: any) => typeof c === 'object' ? loc(c.name) : c).join(', ') : '',
          p.isActive ? 'Aktif' : 'Pasif',
          p.gallery?.length || 0,
          p.externalUrl || '',
          fmtDate(p.createdAt),
          fmtDate(p.updatedAt),
        ])
        const prodSheet = XLSX.utils.aoa_to_sheet([
          ['Ürün Adı', 'Slug', 'Açıklama', 'Kategoriler', 'Durum', 'Görsel Sayısı', 'Harici URL', 'Eklenme', 'Güncelleme'],
          ...prodRows
        ])
        prodSheet['!cols'] = [{ wch: 28 }, { wch: 24 }, { wch: 40 }, { wch: 24 }, { wch: 8 }, { wch: 12 }, { wch: 24 }, { wch: 14 }, { wch: 14 }]
        XLSX.utils.book_append_sheet(wb, prodSheet, 'Ürünler')
      }

      // Sheet 3: Kategoriler
      if (data.categories?.length) {
        const catRows = data.categories.map((c: any) => [
          loc(c.name),
          loc(c.slug),
          loc(c.description),
          c.parentIds?.length ? 'Alt Kategori' : 'Ana Kategori',
          c.orderIndex || 0,
          fmtDate(c.createdAt),
        ])
        const catSheet = XLSX.utils.aoa_to_sheet([
          ['Kategori Adı', 'Slug', 'Açıklama', 'Tür', 'Sıra', 'Eklenme'],
          ...catRows
        ])
        catSheet['!cols'] = [{ wch: 24 }, { wch: 24 }, { wch: 32 }, { wch: 14 }, { wch: 6 }, { wch: 14 }]
        XLSX.utils.book_append_sheet(wb, catSheet, 'Kategoriler')
      }

      // Sheet 4: Blog Yazıları
      if (data.blogPosts?.length) {
        const blogRows = data.blogPosts.map((b: any) => [
          loc(b.title),
          loc(b.slug),
          loc(b.excerpt)?.substring(0, 200),
          typeof b.authorId === 'object' ? (b.authorId?.name || b.authorId?.email || '') : '',
          Array.isArray(b.categories) ? b.categories.map((c: any) => typeof c === 'object' ? loc(c.name) : c).join(', ') : '',
          b.isPublished ? 'Yayında' : 'Taslak',
          b.views || 0,
          b.likes || 0,
          b.readingTime ? `${b.readingTime} dk` : '',
          fmtDate(b.publishedAt),
          fmtDate(b.createdAt),
        ])
        const blogSheet = XLSX.utils.aoa_to_sheet([
          ['Başlık', 'Slug', 'Özet', 'Yazar', 'Kategoriler', 'Durum', 'Görüntülenme', 'Beğeni', 'Okuma Süresi', 'Yayın Tarihi', 'Eklenme'],
          ...blogRows
        ])
        blogSheet['!cols'] = [{ wch: 30 }, { wch: 24 }, { wch: 40 }, { wch: 16 }, { wch: 20 }, { wch: 10 }, { wch: 14 }, { wch: 8 }, { wch: 12 }, { wch: 14 }, { wch: 14 }]
        XLSX.utils.book_append_sheet(wb, blogSheet, 'Blog Yazıları')
      }

      // Sheet 5: İletişim Formları
      if (data.contacts?.length) {
        const contactRows = data.contacts.map((c: any) => [
          c.name || '',
          c.email || '',
          c.phone || '',
          c.company || '',
          c.department || '',
          c.subject || '',
          c.message?.substring(0, 300) || '',
          statusMap[c.status] || c.status || '',
          fmtDate(c.createdAt),
        ])
        const contactSheet = XLSX.utils.aoa_to_sheet([
          ['Ad Soyad', 'E-posta', 'Telefon', 'Şirket', 'Departman', 'Konu', 'Mesaj', 'Durum', 'Tarih'],
          ...contactRows
        ])
        contactSheet['!cols'] = [{ wch: 20 }, { wch: 24 }, { wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 22 }, { wch: 40 }, { wch: 12 }, { wch: 14 }]
        XLSX.utils.book_append_sheet(wb, contactSheet, 'İletişim Formları')
      }

      // Sheet 6: Kariyer Başvuruları
      if (data.applications?.length) {
        const appRows = data.applications.map((a: any) => [
          a.name || '',
          a.email || '',
          a.phone || '',
          a.position || '',
          a.message?.substring(0, 200) || '',
          statusMap[a.status] || a.status || '',
          a.resumeUrl ? 'Var' : 'Yok',
          a.linkedInUrl || '',
          a.kariyerNetUrl || '',
          fmtDate(a.createdAt),
        ])
        const appSheet = XLSX.utils.aoa_to_sheet([
          ['Ad Soyad', 'E-posta', 'Telefon', 'Pozisyon', 'Mesaj', 'Durum', 'CV', 'LinkedIn', 'Kariyer.net', 'Tarih'],
          ...appRows
        ])
        appSheet['!cols'] = [{ wch: 20 }, { wch: 24 }, { wch: 16 }, { wch: 20 }, { wch: 36 }, { wch: 12 }, { wch: 6 }, { wch: 24 }, { wch: 24 }, { wch: 14 }]
        XLSX.utils.book_append_sheet(wb, appSheet, 'Kariyer Başvuruları')
      }

      // Sheet 7: Ekip ve Partnerler
      const miscRows: any[] = []
      if (data.teamMembers?.length) {
        miscRows.push(['--- EKİP ÜYELERİ ---', '', '', ''])
        miscRows.push(['Ad Soyad', 'Pozisyon', 'Sıra', 'Eklenme'])
        data.teamMembers.forEach((t: any) => {
          miscRows.push([loc(t.name), loc(t.position), t.orderIndex || 0, fmtDate(t.createdAt)])
        })
        miscRows.push([])
      }
      if (data.partners?.length) {
        miscRows.push(['--- PARTNERLER ---', '', '', ''])
        miscRows.push(['Partner Adı', 'URL', 'Sıra', 'Eklenme'])
        data.partners.forEach((p: any) => {
          miscRows.push([p.name || loc(p.name), p.url || p.websiteUrl || '', p.orderIndex || 0, fmtDate(p.createdAt)])
        })
      }
      if (miscRows.length) {
        const miscSheet = XLSX.utils.aoa_to_sheet(miscRows)
        miscSheet['!cols'] = [{ wch: 24 }, { wch: 28 }, { wch: 8 }, { wch: 14 }]
        XLSX.utils.book_append_sheet(wb, miscSheet, 'Ekip & Partnerler')
      }

      const dateStr = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(wb, `SKG_Metal_Tam_Rapor_${dateStr}.xlsx`)
      toast.success('Rapor başarıyla indirildi')
    } catch (err) {
      console.error(err)
      toast.error('Rapor oluşturulurken bir hata oluştu')
    } finally {
      setIsExporting(false)
    }
  }, [])

  const handleNewProduct = useCallback(() => {
    navigate({ to: '/urunler' })
  }, [navigate])

  const trendData = stats?.trends || []

  if (isStatsLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[60vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        {/* Page Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">İyi Günler, Admin 👋</h1>
              <p className="text-sm text-muted-foreground">SKG Metal yönetim panelinize hoş geldiniz. Ürünlerinizi ve operasyonlarınızı yönetin.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Yenile
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportReport} disabled={isExporting || !stats}>
              {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Rapor
            </Button>
            <Button size="sm" onClick={handleNewProduct}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Ürün
            </Button>
          </div>
        </div>

        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Toplam Ürün"
            value={stats ? stats.summary.products.toString() : '...'}
            subtitle={`${stats?.summary.products || 0} toplam ürün`}
            icon={Package}
            loading={isStatsLoading}
          />
          <StatCard
            title="Aktif Kategori"
            value={stats ? stats.summary.categories.toString() : '...'}
            subtitle={`${stats?.summary.categories || 0} toplam kategori`}
            icon={Box}
            loading={isStatsLoading}
          />
          <StatCard
            title="Blog Yazıları"
            value={stats ? stats.summary.blog.toString() : '...'}
            subtitle={`${stats?.summary.blog || 0} toplam yazı`}
            icon={Award}
            variant="success"
            loading={isStatsLoading}
          />
          <StatCard
            title="Ekip Üyeleri"
            value={stats ? stats.summary.team.toString() : '...'}
            subtitle={`${stats?.summary.team || 0} toplam üye`}
            icon={Users}
            variant="success"
            loading={isStatsLoading}
          />
        </div>

        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Bekleyen Başvurular"
            value={stats ? stats.summary.applications.new.toString() : '...'}
            subtitle={`${stats?.summary.applications.total || 0} toplam başvuru`}
            icon={Clock}
            variant="warning"
            loading={isStatsLoading}
          />
          <StatCard
            title="İş Ortakları"
            value={stats ? stats.summary.partners.toString() : '...'}
            subtitle={`${stats?.summary.partners || 0} toplam partner`}
            icon={MapPin}
            loading={isStatsLoading}
          />
          <StatCard
            title="Yeni Mesajlar"
            value={stats ? stats.summary.contacts.new.toString() : '...'}
            subtitle={`${stats?.summary.contacts.total || 0} toplam mesaj`}
            icon={CheckCircle2}
            variant="destructive"
            loading={isStatsLoading}
          />
          <StatCard
            title="Kariyer Başarı"
            value={stats ? stats.summary.applications.total.toString() : '...'}
            subtitle={`${stats?.summary.applications.total || 0} toplam aday`}
            icon={TrendingUp}
            loading={isStatsLoading}
          />
        </div>

        <Tabs defaultValue="overview" className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto pb-1 -mx-1 px-1">
            <TabsList className="inline-flex min-w-max">
              <TabsTrigger value="overview" className="gap-1.5 sm:gap-2 px-2.5 sm:px-3">
                <Activity className="h-4 w-4" />
                <span>Genel</span>
              </TabsTrigger>
              <TabsTrigger value="products" className="gap-1.5 sm:gap-2 px-2.5 sm:px-3">
                <Package className="h-4 w-4" />
                <span className="hidden sm:inline">Ürünler</span>
                <span className="sm:hidden">Ürün</span>
              </TabsTrigger>
              <TabsTrigger value="categories" className="gap-1.5 sm:gap-2 px-2.5 sm:px-3">
                <Box className="h-4 w-4" />
                <span>Kategoriler</span>
              </TabsTrigger>
              <TabsTrigger value="applications" className="gap-1.5 sm:gap-2 px-2.5 sm:px-3">
                <Users className="h-4 w-4" />
                <span>Başvurular</span>
              </TabsTrigger>
              <TabsTrigger value="messages" className="gap-1.5 sm:gap-2 px-2.5 sm:px-3">
                <CheckCircle2 className="h-4 w-4" />
                <span>Mesajlar</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4 sm:space-y-6">
            <div className="grid gap-4 sm:gap-6 lg:grid-cols-7">
              {/* Left Column - 4/7 */}
              <div className="space-y-4 sm:space-y-6 lg:col-span-4">
                {/* Shipment Trends */}
                <Card>
                  <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pb-2">
                    <div>
                      <CardTitle className="text-base">Ürün Trendi</CardTitle>
                      <CardDescription>Son 6 aydaki ürün ekleme istatistikleri</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Yenile
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="h-56 sm:h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trendData}>
                          <defs>
                            <linearGradient id="shipmentGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="deliveryGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                          <Area type="monotone" dataKey="urun" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#shipmentGradient)" name="Ürün Sayısı" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Active Shipments */}
                <Card>
                  <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pb-2">
                    <div>
                      <CardTitle className="text-base">Son Eklenen Ürünler</CardTitle>
                      <CardDescription>{stats?.recent.products.length || 0} adet yeni ürün</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/urunler' })}>
                      Tümü <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="p-0 sm:p-6 sm:pt-0">
                    {/* Mobile View */}
                    {/* Mobile View */}
                    <div className="block sm:hidden space-y-3 px-4 pb-4">
                      {isStatsLoading ? (
                        <div className="flex h-32 items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        stats?.recent.products.map((product) => (
                          <div key={product._id} className="rounded-lg border bg-card p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-primary text-xs truncate max-w-[100px]">{product._id}</span>
                              <StatusBadge status="Aktif" />
                            </div>
                            <p className="text-sm font-medium mb-1 truncate">{product.title?.tr}</p>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                              <Package className="h-3 w-3" />
                              <span>{product.categoryIds?.[0]?.name?.tr || 'Genel'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-muted-foreground">{new Date(product.createdAt).toLocaleDateString('tr-TR')}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Desktop View */}
                    <div className="hidden sm:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Ürün Adı</TableHead>
                            <TableHead>Kategori</TableHead>
                            <TableHead>Durum</TableHead>
                            <TableHead>Eklenme Tarihi</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {isStatsLoading ? (
                            <TableRow>
                              <TableCell colSpan={4} className="h-32 text-center">
                                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                              </TableCell>
                            </TableRow>
                          ) : (
                            stats?.recent.products.map((product) => (
                              <TableRow key={product._id}>
                                <TableCell className="font-medium truncate max-w-[200px]">{product.title?.tr}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1.5 text-sm">
                                    <Package className="h-3 w-3 text-muted-foreground" />
                                    <span>{product.categoryIds?.[0]?.name?.tr || 'Genel'}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <StatusBadge status="Aktif" />
                                </TableCell>
                                <TableCell>
                                  <span className="text-xs text-muted-foreground">{new Date(product.createdAt).toLocaleDateString('tr-TR')}</span>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Messages */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Son Mesajlar</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 sm:space-y-4">
                      {isStatsLoading ? (
                        <div className="flex h-32 items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        stats?.recent.contacts.map((contact) => (
                          <div key={contact._id} className="flex items-start gap-2 sm:gap-3">
                            <div className={`mt-0.5 rounded-full bg-muted p-1.5 sm:p-2 shrink-0 ${contact.status === 'new' ? 'text-primary' : 'text-muted-foreground'}`}>
                              <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm font-medium">{contact.name}</p>
                              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{contact.subject}</p>
                            </div>
                            <span className="text-[10px] sm:text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                              {new Date(contact.createdAt).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - 3/7 */}
              <div className="space-y-4 sm:space-y-6 lg:col-span-3">
                {/* Quick Actions / Info */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Hızlı İstatistikler</CardTitle>
                    <CardDescription>Operasyonel özet verilieri</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Yeni Başvurular</span>
                        <Badge variant="destructive">{stats?.summary.applications.new || 0}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Yeni Mesajlar</span>
                        <Badge variant="destructive">{stats?.summary.contacts.new || 0}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Toplam Ürün</span>
                        <Badge variant="outline">{stats?.summary.products || 0}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Category Distribution */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Kategori Dağılımı</CardTitle>
                    <CardDescription>Ürünlerin kategorilere göre dağılımı</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isStatsLoading ? (
                      <div className="flex h-48 items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        <div className="h-40 sm:h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={stats?.distribution.categories || []}
                                cx="50%"
                                cy="50%"
                                innerRadius={45}
                                outerRadius={70}
                                paddingAngle={4}
                                dataKey="value"
                              >
                                {(stats?.distribution.categories || []).map((_, index) => (
                                  <Cell key={index} fill={`hsl(var(--chart-${(index % 5) + 1}))`} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="mt-2 space-y-2">
                          {(stats?.distribution.categories || []).slice(0, 5).map((item, index) => (
                            <div key={item.name} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: `hsl(var(--chart-${(index % 5) + 1}))` }} />
                                <span className="truncate max-w-[120px]">{item.name}</span>
                              </div>
                              <span className="font-medium">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Applications Summary */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Son Başvurular</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 sm:space-y-3">
                      {isStatsLoading ? (
                        <div className="flex h-32 items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        stats?.recent.applications.map((app) => (
                          <div key={app._id} className="flex items-center justify-between rounded-lg bg-muted/50 p-2.5 sm:p-3 gap-2">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className="flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs sm:text-sm font-medium truncate">{app.name}</p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{app.position}</p>
                              </div>
                            </div>
                            <Badge variant="secondary" className="shrink-0 text-[10px] sm:text-xs">
                              {app.status === 'new' ? 'Yeni' : app.status}
                            </Badge>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* PRODUCTS TAB */}
          <TabsContent value="products" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Sistemdeki Ürünler</CardTitle>
                <CardDescription>Son eklenen ürünlerin detaylı listesi</CardDescription>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ürün Adı</TableHead>
                        <TableHead>Kategori</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Tarih</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isStatsLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} className="h-32 text-center">
                            <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ) : (
                        stats?.recent.products.map((product) => (
                          <TableRow key={product._id}>
                            <TableCell className="font-medium">{product.title?.tr}</TableCell>
                            <TableCell>{product.categoryIds?.[0]?.name?.tr || 'Genel'}</TableCell>
                            <TableCell><StatusBadge status="Aktif" /></TableCell>
                            <TableCell>{new Date(product.createdAt).toLocaleDateString('tr-TR')}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CATEGORIES TAB */}
          <TabsContent value="categories" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Kategori Dağılımı</CardTitle>
                <CardDescription>Aktif kategoriler ve ürün sayıları</CardDescription>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kategori Adı</TableHead>
                        <TableHead>Kategori Tipi</TableHead>
                        <TableHead>Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isStatsLoading ? (
                        <TableRow>
                          <TableCell colSpan={3} className="h-32 text-center">
                            <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ) : (
                        stats?.distribution.categories.map((cat) => (
                          <TableRow key={cat.name}>
                            <TableCell className="font-medium">{cat.name}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="font-normal">
                                {cat.type}
                              </Badge>
                            </TableCell>
                            <TableCell><StatusBadge status="Aktif" /></TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MESSAGES TAB */}
          <TabsContent value="messages" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Son Mesajlar</CardTitle>
                <CardDescription>İletişim formundan gelen son mesajlar</CardDescription>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ad Soyad</TableHead>
                        <TableHead>E-posta</TableHead>
                        <TableHead>Konu</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isStatsLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="h-32 text-center">
                            <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ) : (
                        stats?.recent.contacts.map((contact) => (
                          <TableRow key={contact._id}>
                            <TableCell className="font-medium">{contact.name}</TableCell>
                            <TableCell>{contact.email}</TableCell>
                            <TableCell>{contact.subject}</TableCell>
                            <TableCell>{new Date(contact.createdAt).toLocaleDateString('tr-TR')}</TableCell>
                            <TableCell>
                              <Badge variant={contact.status === 'new' ? 'destructive' : 'secondary'}>
                                {contact.status === 'new' ? 'Yeni' : 'Okundu'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* APPLICATIONS TAB */}
          <TabsContent value="applications" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Kariyer Başvuruları</CardTitle>
                <CardDescription>Son iş başvuruları ve adaylar</CardDescription>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ad Soyad</TableHead>
                        <TableHead>Pozisyon</TableHead>
                        <TableHead>Profil Linkleri</TableHead>
                        <TableHead>E-posta</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isStatsLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="h-32 text-center">
                            <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ) : (
                        stats?.recent.applications.map((app) => (
                          <TableRow key={app._id}>
                            <TableCell className="font-medium">{app.name}</TableCell>
                            <TableCell>{app.position}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {app.linkedInUrl && (
                                  <a href={app.linkedInUrl} target="_blank" rel="noreferrer" title="LinkedIn">
                                    <Badge variant="outline" className="cursor-pointer hover:bg-blue-50">LI</Badge>
                                  </a>
                                )}
                                {app.kariyerNetUrl && (
                                  <a href={app.kariyerNetUrl} target="_blank" rel="noreferrer" title="Kariyer.net">
                                    <Badge variant="outline" className="cursor-pointer hover:bg-orange-50">KN</Badge>
                                  </a>
                                )}
                                {!app.linkedInUrl && !app.kariyerNetUrl && <span className="text-muted-foreground">-</span>}
                              </div>
                            </TableCell>
                            <TableCell>{app.email}</TableCell>
                            <TableCell>{new Date(app.createdAt).toLocaleDateString('tr-TR')}</TableCell>
                            <TableCell>
                              <Badge variant={app.status === 'new' ? 'destructive' : 'secondary'}>
                                {app.status === 'new' ? 'Yeni' : app.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </Main>
    </>
  )
}

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: true },
]

export const Route = createFileRoute('/_authenticated/')({
  component: Panel,
})
